/**
 * FDV — prueba A/B `carrito_v2`: barra fija «Ir a pagar» directo al checkout, sin cajón automático.
 *
 * Especificación: flor_venus_comercial_intelligence,
 * 2026/10-octubre/campanas/2026-10-26_prueba-carrito-v2/README.md.
 * Viene de `carrito_v1` (22–26 oct): el cajón que se abría solo al desbloquear una oferta interrumpía a quien
 * seguía eligiendo (B compró 10.5% contra 22.7%). En v2 el cajón nunca se abre solo.
 *
 * - **A (control):** no cambia nada en pantalla. Solo registra su exposición.
 * - **B:** desde el primer producto pagado aparece una barra fija abajo (piezas, subtotal y un mensaje de regalos:
 *   cuánto falta para el siguiente si está cerca, o lo ya ganado). «Ir a pagar» lleva directo al checkout; tocar el
 *   resto de la barra abre el cajón.
 *   El aviso de regalo (`fdv-gift-toast.js`) es el mismo para A y B; en B solo sube para no tapar la barra.
 *
 * Reparto: FNV-1a de `carrito_v2:<anon_id>` % 100; < 50 → A. Se guarda en `localStorage.fdv_exp_carrito_v2`.
 * Exposición: `fdv_experiment_exposed` una vez por navegador, cuando la visitante tiene por primera vez un producto
 * pagado en el carrito dentro de la ventana.
 *
 * Corre solo en el tema publicado, en el dominio canónico y dentro de la ventana de los ajustes del tema
 * («FDV — Prueba del carrito»). Fuera de eso todas ven A y no se registra nada. Bots (`navigator.webdriver`) y
 * navegadores del equipo (`localStorage.fdv_internal = '1'`) quedan fuera.
 * Escotilla de prueba (vista previa): `?fdv_exp=B` en la URL (o `localStorage.fdv_exp_carrito_force`) → corre en cualquier tema y
 * fuera de la ventana; su exposición sale con `forzado: true` para excluirla del análisis.
 */
(() => {
  const EXP = 'carrito_v2';
  const KEY = 'fdv_exp_carrito_v2';
  const KEY_EXPUESTO = 'fdv_exp_carrito_v2_expuesto';
  const KEY_FORCE = 'fdv_exp_carrito_force';

  // Marcar el navegador del equipo con un enlace: `?fdv_internal=1` lo marca, `?fdv_internal=0` lo desmarca.
  try {
    const marca = new URLSearchParams(location.search).get('fdv_internal');
    if (marca === '1') {
      localStorage.setItem('fdv_internal', '1');
      console.info('[FDV] Este navegador quedó marcado como del equipo (fuera de las pruebas).');
    } else if (marca === '0') {
      localStorage.removeItem('fdv_internal');
      console.info('[FDV] Este navegador ya no está marcado como del equipo.');
    }
  } catch (e) {
    /* almacenamiento bloqueado */
  }

  const FDV = window.FDV;
  const cfgEl = document.getElementById('fdv-exp-carrito-config');
  if (!FDV || !cfgEl) return;
  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }

  const store = (area, op, key, value) => {
    try {
      const s = window[area];
      if (op === 'get') return s.getItem(key);
      s.setItem(key, value);
    } catch (e) {
      /* almacenamiento bloqueado: se sigue sin recordar */
    }
    return null;
  };

  // --- Quién corre la prueba -------------------------------------------------------------------------------
  // Para revisar en un teléfono: `?fdv_exp=A` o `?fdv_exp=B` fuerza el grupo en ese navegador; `?fdv_exp=off` lo quita.
  try {
    const q = new URLSearchParams(location.search).get('fdv_exp');
    if (q === 'A' || q === 'B') store('localStorage', 'set', KEY_FORCE, q);
    else if (q === 'off') localStorage.removeItem(KEY_FORCE);
  } catch (e) {
    /* noop */
  }
  const forcedRaw = store('localStorage', 'get', KEY_FORCE);
  const forced = forcedRaw === 'A' || forcedRaw === 'B' ? forcedRaw : null;

  const stripWww = (h) => (h || '').replace(/^www\./, '');
  let primaryHost = '';
  try {
    if (cfg.primaryUrl) primaryHost = stripWww(new URL(cfg.primaryUrl).hostname);
  } catch (e) {
    /* noop */
  }
  const here = stripWww(location.hostname);
  const themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
  const onProd =
    themeRole === 'main' &&
    here !== '' &&
    !/\.(myshopify\.com|shopifypreview\.com)$/.test(here) &&
    ((primaryHost !== '' && here === primaryHost) || here === 'flordevenus.com.mx');

  const t0 = Date.parse(cfg.inicio || '');
  const t1 = Date.parse(cfg.fin || '');
  const inWindow = cfg.activo === true && !isNaN(t0) && !isNaN(t1) && Date.now() >= t0 && Date.now() < t1;
  const excluded = navigator.webdriver === true || store('localStorage', 'get', 'fdv_internal') === '1';

  let variant;
  if (forced) {
    variant = forced;
  } else {
    if (!onProd || !inWindow || excluded || !FDV.anonId) return;
    variant = store('localStorage', 'get', KEY);
    if (variant !== 'A' && variant !== 'B') {
      variant = fnv1a(`${EXP}:${FDV.anonId}`) % 100 < 50 ? 'A' : 'B';
      store('localStorage', 'set', KEY, variant);
    }
  }
  FDV.experiments = Object.assign({}, FDV.experiments, { [EXP]: variant });

  function fnv1a(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }

  const track = (name, props) => {
    try {
      FDV.track(name, Object.assign({ experiment: EXP, variant, forzado: !!forced }, props));
    } catch (e) {
      /* el tracking nunca rompe la página */
    }
  };

  // --- Carrito (mismo cálculo que el motor de regalos) ------------------------------------------------------
  const isGift = (it) => !!(it.properties && it.properties._gift);
  const paidQty = (cart) =>
    (cart?.items || []).reduce((n, it) => n + (!isGift(it) && (it.final_line_price ?? it.line_price ?? 0) > 0 ? it.quantity : 0), 0);

  function merchSubtotal(cart) {
    const items = cart?.items || [];
    if (!items.length) return 0;
    const lines = items.reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
    const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, app) => {
      const da = app.discount_application || app;
      if (da.target_type === 'shipping_line') return sum;
      return sum + (app.total_allocated_amount ?? app.amount ?? da.total_allocated_amount ?? 0);
    }, 0);
    return Math.max(0, lines - cartDiscounts);
  }

  const money = (cents) => {
    const v = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      }).format(v);
    } catch (e) {
      return `$${Math.round(v)}`;
    }
  };

  // --- Exposición -------------------------------------------------------------------------------------------
  let sawAdd = false;
  document.addEventListener('shopify:cart:lines-update', (e) => {
    if (!e || e.action === undefined || e.action === 'add') {
      sawAdd = true;
      // B: la barra responde al instante; Shopify tarda ≈ 0.9 s en confirmar y el motor ≈ 0.3 s más.
      // El motor de regalos despacha sus propios cambios como `update`, así que aquí solo llegan los de la clienta.
      if (variant === 'B' && e && e.action === 'add') showPending(e);
    }
  });

  const exposedKey = forced ? `${KEY_EXPUESTO}_forzado` : KEY_EXPUESTO;
  function maybeExpose(cart) {
    if (store('localStorage', 'get', exposedKey) === '1') return;
    const n = paidQty(cart);
    if (n <= 0) return;
    store('localStorage', 'set', exposedKey, '1');
    track('fdv_experiment_exposed', {
      motivo: sawAdd ? 'agrego' : 'carrito_previo',
      piezas_pagadas: n,
      subtotal: Math.round(merchSubtotal(cart) / 100),
    });
  }

  // --- Abrir el cajón ---------------------------------------------------------------------------------------
  const isCartPage = cfg.template === 'cart';

  function openDrawer(origen) {
    FDV.cartDrawerOrigenPista = { origen, t: performance.now() };
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const icon = document.querySelector('cart-icon button, cart-icon a, header a[href$="/cart"]');
    if (icon) icon.click();
    else location.href = '/cart';
  }

  // --- B: barra fija ----------------------------------------------------------------------------------------
  let bar = null;
  let els = null;
  const OTRAS_BARRAS = ['.sticky-add-to-cart__bar[data-stuck="true"]', '.fdv-hero-sticky[data-visible]'];

  function buildBar() {
    bar = document.createElement('div');
    bar.className = 'fdv-xcart';
    bar.hidden = true;
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Tu carrito');
    bar.innerHTML =
      '<button type="button" class="fdv-xcart__info" data-xcart="barra">' +
      '<span class="fdv-xcart__line" aria-live="polite"><span aria-hidden="true">🛒</span> <span data-xcart-n></span> · <span data-xcart-sub></span></span>' +
      '<span class="fdv-xcart__msg" data-xcart-msg></span>' +
      '</button>' +
      '<button type="button" class="button fdv-xcart__btn" data-xcart="ir_a_pagar">Ir a pagar</button>';
    els = {
      n: bar.querySelector('[data-xcart-n]'),
      sub: bar.querySelector('[data-xcart-sub]'),
      msg: bar.querySelector('[data-xcart-msg]'),
    };
    bar.addEventListener('click', (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-xcart]') : null;
      if (!btn) return;
      const accion = btn.getAttribute('data-xcart');
      track('fdv_exp_carrito_click', { accion });
      // «Ir a pagar» hace lo que dice: los regalos ya van en el carrito y los códigos se pueden poner en el checkout.
      if (accion === 'ir_a_pagar') location.href = '/checkout';
      else openDrawer('barra');
    });
    document.body.appendChild(bar);
  }

  // Lo que la ventana mide por debajo del área visible (p. ej. la barra de Safari encima del contenido en iPhone).
  function hiddenBottom() {
    const vv = window.visualViewport;
    if (!vv) return 0;
    return Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
  }

  function placeBar() {
    if (!bar || bar.hidden) return;
    let offset = 0;
    const vh = window.innerHeight;
    for (const sel of OTRAS_BARRAS) {
      document.querySelectorAll(sel).forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.bottom >= vh - 2) offset = Math.max(offset, vh - r.top);
      });
    }
    offset = Math.max(offset, hiddenBottom());
    bar.style.setProperty('--fdv-xcart-offset', `${Math.round(offset)}px`);
    document.documentElement.style.setProperty('--fdv-xcart-stack', `${Math.round(offset + bar.offsetHeight)}px`);
    if (debug) renderDebug(offset);
  }

  // Diagnóstico en el teléfono: `?fdv_exp_debug=1` muestra arriba las medidas (`?fdv_exp_debug=0` lo quita).
  let debug = false;
  let debugBox = null;
  try {
    const q = new URLSearchParams(location.search).get('fdv_exp_debug');
    if (q === '1') sessionStorage.setItem('fdv_exp_debug', '1');
    if (q === '0') sessionStorage.removeItem('fdv_exp_debug');
    debug = sessionStorage.getItem('fdv_exp_debug') === '1';
  } catch (e) {
    /* noop */
  }
  function renderDebug(offset) {
    if (!debugBox) {
      debugBox = document.createElement('pre');
      debugBox.style.cssText =
        'position:fixed;top:4px;left:4px;z-index:2147483647;margin:0;padding:6px 8px;font:11px/1.35 monospace;background:rgba(0,0,0,.8);color:#7CFC00;border-radius:6px;pointer-events:none;white-space:pre';
      document.body.appendChild(debugBox);
    }
    const vv = window.visualViewport;
    const r = bar.getBoundingClientRect();
    const sab = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '?';
    debugBox.textContent = [
      `grupo ${variant}${forced ? ' (forzado)' : ''}`,
      `innerH ${window.innerHeight}  vv.h ${vv ? Math.round(vv.height) : '-'}  vv.top ${vv ? Math.round(vv.offsetTop) : '-'}`,
      `oculto abajo ${hiddenBottom()}  offset ${Math.round(offset)}  safe ${sab.trim()}`,
      `barra top ${Math.round(r.top)} bottom ${Math.round(r.bottom)} alto ${Math.round(r.height)}`,
      `scrollY ${Math.round(window.scrollY)}`,
    ].join('\n');
  }

  let rafPending = false;
  let settleTimer = null;
  const schedulePlace = () => {
    // Las otras barras entran y salen con transición: reajustar también cuando terminan de moverse.
    clearTimeout(settleTimer);
    settleTimer = setTimeout(placeBar, 400);
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      placeBar();
    });
  };

  const piezas = (n) => `${n} ${n === 1 ? cfg.singular || 'vela' : cfg.plural || 'velas'}`;
  let lastPaid = 0;
  let pendingTimer = null;

  // Respuesta inmediata al agregar: cuenta provisional y «Agregando…» hasta que llega el estado real.
  function showPending(e) {
    if (isCartPage) return;
    const qty = (e.lines || []).reduce((sum, l) => sum + (Number(l.quantity) || 0), 0) || 1;
    if (!bar) buildBar();
    els.sub.textContent = '…';
    els.n.textContent = piezas(lastPaid + qty);
    els.msg.textContent = 'Agregando…';
    bar.classList.add('is-pending');
    bar.hidden = false;
    schedulePlace();
    // Si la adición falla o el motor no publica, volver al último estado conocido.
    const restore = () => {
      if (!bar.classList.contains('is-pending')) return;
      if (FDV.gifts && FDV.gifts.cart) renderBar(FDV.gifts.state, FDV.gifts.cart);
      else bar.hidden = true;
    };
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(restore, 5000);
    if (e.promise && typeof e.promise.then === 'function') {
      e.promise.then((r) => r && r.detail && r.detail.didError && restore()).catch(restore);
    }
  }

  function renderBar(state, cart) {
    const n = paidQty(cart);
    lastPaid = n;
    if (bar) bar.classList.remove('is-pending');
    clearTimeout(pendingTimer);
    if (isCartPage || n <= 0) {
      if (bar) bar.hidden = true;
      return;
    }
    if (!bar) buildBar();
    els.n.textContent = piezas(n);
    els.sub.textContent = money(merchSubtotal(cart));
    const m = barMessage(state);
    els.msg.textContent = m.texto;
    if (m.clave !== lastMsgKey) {
      lastMsgKey = m.clave;
      if (m.tipo) track('fdv_exp_carrito_msg', { tipo: m.tipo, offer_id: m.offerId || null, faltan: m.faltan ?? null });
    }
    bar.hidden = false;
    schedulePlace();
  }

  // --- Mensaje de regalos (B): un solo mensaje a la vez -----------------------------------------------------
  // Misma escalera que el cajón (`fdv-gift-progress.js`): ofertas por monto, ordenadas por umbral, sin las de
  // cantidad. Si al siguiente premio le falta ≤ 20% de su umbral → «Te faltan $X para …» (empuja a agregar).
  // Si está lejos → lo que ya ganó (empuja a pagar). Sin nada ganado y lejos → cuánto falta para el envío gratis.
  const CERCA = 0.2;
  let lastMsgKey = '';

  function rewardLabel(s) {
    if (s.rewardLabel) return s.rewardLabel;
    if (s.mechanism === 'free_shipping_message') return 'Envío gratis';
    if (s.mystery) return 'Regalo sorpresa';
    const t = (s.giftLineLabel || 'Tu regalo')
      .replace(/^[^\p{L}]+/u, '')
      .replace(/\s*(¡?gratis!?|de regalo)\s*/giu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Tu regalo';
  }
  // Nombre sin «gratis» / «de regalo» para listar varios; el envío conserva «gratis» (sin él no dice nada).
  const bare = (s) =>
    s.mechanism === 'free_shipping_message'
      ? 'Envío gratis'
      : rewardLabel(s)
          .replace(/^[^\p{L}]+/u, '')
          .replace(/\s*(¡?gratis!?|de regalo)\s*$/iu, '')
          .trim();

  function barMessage(state) {
    const escalera = (state || [])
      .filter((s) => s.kind === 'threshold' && s.threshold > 0 && s._offer?.triggerType !== 'collection_quantity')
      .sort((a, b) => a.threshold - b.threshold);
    if (!escalera.length) return { texto: '', clave: '' };
    const ganados = escalera.filter((s) => s.status === 'unlocked');
    const siguiente = escalera.find((s) => s.status !== 'unlocked');
    if (!siguiente) return { texto: '🎁 Tienes todos los regalos', clave: 'todo', tipo: 'todo' };
    // Las ofertas con el mismo umbral se nombran juntas («Poción + Gema»).
    const mismos = escalera.filter((s) => s.threshold === siguiente.threshold && s.status !== 'unlocked');
    const nombre = mismos.map(bare).join(' + ');
    const faltan = siguiente.remainingAmount || 0;
    if (faltan <= siguiente.threshold * CERCA) {
      return {
        texto: `Te faltan ${money(faltan)} para ${siguiente.mechanism === 'free_shipping_message' ? 'envío gratis' : nombre}`,
        clave: `cerca:${siguiente.id}`,
        tipo: 'cerca',
        offerId: siguiente.id,
        faltan: Math.round(faltan / 100),
      };
    }
    if (ganados.length) {
      return { texto: `🎁 ${ganados.map(bare).join(' + ')}`, clave: `ganado:${ganados.map((s) => s.id).join(',')}`, tipo: 'ganado' };
    }
    const envio = escalera.find((s) => s.mechanism === 'free_shipping_message');
    if (envio) return { texto: `Te faltan ${money(envio.remainingAmount)} para envío gratis`, clave: 'envio', tipo: 'envio' };
    return { texto: '', clave: '' };
  }

  // --- Estado del carrito -----------------------------------------------------------------------------------
  function onState(state, cart) {
    if (!cart) return;
    maybeExpose(cart);
    if (variant === 'B') renderBar(state, cart);
  }

  document.addEventListener('fdv:gifts:state', (e) => onState(e.detail && e.detail.state, e.detail && e.detail.cart));
  if (FDV.gifts && FDV.gifts.cart) onState(FDV.gifts.state, FDV.gifts.cart);

  if (variant === 'B') {
    window.addEventListener('scroll', schedulePlace, { passive: true });
    window.addEventListener('resize', schedulePlace);
    // iPhone: la barra de Safari crece y se encoge al hacer scroll sin disparar `resize` en la ventana.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', schedulePlace);
      window.visualViewport.addEventListener('scroll', schedulePlace);
    }
  }
})();
